/* eslint-disable @typescript-eslint/no-explicit-any */
import DataTable from "@/components/data-table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGetDomainReputationData } from "@/hooks/use-postmaster";
import { cn } from "@/lib/utils";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { type ColDef } from "ag-grid-community";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { useMemo } from "react";
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

function DomainTabContent({ date, domain }: { date: string; domain: string }) {
  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    const start = new Date();

    switch (date) {
      case "last7days":
        start.setDate(end.getDate() - 7);
        break;
      case "last30days":
        start.setDate(end.getDate() - 30);
        break;
      case "last60days":
        start.setDate(end.getDate() - 60);
        break;
      case "last90days":
        start.setDate(end.getDate() - 90);
        break;
      case "last120days":
        start.setDate(end.getDate() - 120);
        break;
      default:
        start.setDate(end.getDate() - 7);
    }

    return { startDate: start, endDate: end };
  }, [date]);

  const { data, isLoading } = useGetDomainReputationData({
    domain,
    startDate,
    endDate,
  });

  const columnDefs: ColDef[] = [
    {
      field: "date",
      headerName: "Date",
      filter: true,
      sortable: true,
      valueFormatter: (params) =>
        format(new Date(params.value), "MMM dd, yyyy"),
    },
    {
      field: "reputation",
      headerName: "Domain reputation",
      filter: true,
      sortable: true,
      cellRenderer: (params: any) => {
        const colorMap: Record<string, string> = {
          HIGH: "bg-emerald-500 hover:bg-emerald-500",
          MEDIUM: "bg-yellow-500 hover:bg-yellow-500",
          LOW: "bg-red-500 hover:bg-red-500",
          BAD: "bg-red-900 hover:bg-red-900",
          UNKNOWN: "bg-gray-500 hover:bg-gray-500",
        };
        const status = params.value as keyof typeof colorMap;
        return (
          <Badge className={cn("shadow-none", colorMap[status])}>
            {params.value}
          </Badge>
        );
      },
    },
  ];

  const formattedChartData = data?.chartData.map((item) => ({
    ...item,
    date: format(new Date(item.date), "MMM dd, yyyy"),
  }));

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-[50vh] w-full text-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">
              Current Reputation
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex items-center gap-2">
              <Badge
                className={`shadow-none ${
                  data?.statistics.currentReputation === "HIGH"
                    ? "bg-emerald-500"
                    : "bg-yellow-500"
                }`}
              >
                {data?.statistics.currentReputation}
              </Badge>
              <span className="text-2xl font-bold">
                {(data?.statistics.reputationScore || 0) * 25}%
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {data?.statistics.currentReputation === "HIGH"
                ? "Consistent high reputation"
                : "Room for improvement"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">Domain Status</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div
              className={`text-sm font-medium ${
                data?.statistics.status === "Excellent"
                  ? "text-green-600"
                  : "text-yellow-600"
              }`}
            >
              {data?.statistics.status}
            </div>
            <p className="text-xs text-muted-foreground">
              {data?.statistics.status === "Excellent"
                ? "All metrics positive"
                : "Needs attention"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">
              Monitoring Period
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-2xl font-bold">
              {data?.statistics.monitoringPeriod}
            </div>
            <p className="text-xs text-muted-foreground">Rolling window</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium">Trend</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="text-sm font-medium text-green-600">
              {data?.statistics.currentReputation === "HIGH"
                ? "Stable"
                : "Variable"}
            </div>
            <p className="text-xs text-muted-foreground">
              {data?.statistics.currentReputation === "HIGH"
                ? "No fluctuations"
                : "Shows fluctuations"}
            </p>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader className="p-4">
          <CardTitle>Domain Reputation Trend</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={formattedChartData}
                margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
              >
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12 }}
                  tickMargin={10}
                  // interval="preserveStartEnd"
                  // angle={-45}
                  // textAnchor="end"
                  // height={60}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  ticks={[0, 1, 2, 3, 4]}
                  domain={[0, 4]}
                  tickFormatter={(value) =>
                    data?.reputationLevels[value - 1] || ""
                  }
                  tick={{ fontSize: 12 }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  dot={{ fill: "#3B82F6", strokeWidth: 2, r: 4 }}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--background))",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                  }}
                  formatter={(value: number) => [
                    data?.reputationLevels[value - 1],
                    "Reputation",
                  ]}
                  labelFormatter={(label) => label}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      <div className="mt-6">
        <DataTable
          rowData={data?.chartData || []}
          columnDefs={columnDefs}
          height={400}
        />
      </div>
    </div>
  );
}

// Export both the component for use in other files and the Route for the router
export { DomainTabContent };

function DomainTabRouteComponent() {
  // Get domain and date from search params if available, otherwise use defaults
  const search = useSearch({ strict: false });
  const domain = (search as { domain?: string })?.domain || "";
  const date = (search as { date?: string })?.date || "last7days";
  return <DomainTabContent date={date} domain={domain} />;
}

export const Route = createFileRoute("/_private/postmaster/tabs/domain-tab")({
  component: DomainTabRouteComponent,
});
